import { useCallback } from 'react';

import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import ZodErrorToast from '~/components/ZodErrorToast';
import { getZodErrorFromResponse } from '~/components/ZodErrorToast/zodError';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useCallbackWithErrorHandler<T extends (...args: any[]) => Promise<any>>(
    fn: T,
    deps: React.DependencyList = [],
    message?: string,
) {
    const { t } = useTranslation();
    return useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        async (...params: any[]): Promise<any> => {
            try {
                const response = await fn(...params);
                if (response instanceof Response && !response.ok) toast.error(message || t('TOAST.ERROR_OCCURRED'));
                return response;
            } catch (error) {
                Sentry.captureException(error);
                if (
                    typeof error === 'object' &&
                    error != null &&
                    'response' in error &&
                    typeof error.response === 'string'
                ) {
                    const zodError = getZodErrorFromResponse(error?.response);
                    if (zodError) {
                        toast.error(<ZodErrorToast zodError={zodError} />);
                    }
                }
                toast.error(message || t('TOAST.ERROR_OCCURRED'));
                return undefined;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fn, message, t, ...deps],
    );
}

export default useCallbackWithErrorHandler;
